import { useDescope, useSession } from '@descope/react-sdk';
import { FEATURE_FLAGS_DEFAULTS } from 'featureFlagsDefaults';
import { useQuery } from 'react-query';
import { useProjectCtx } from 'state/project/ProjectCtx';
import {
	FeatureFlagId,
	IReqFeatureFlags,
	IResFeatureFlags,
	IServerFeatureFlags
} from 'types/featureflags';
import { adminPortalGet, adminPortalPost, CSRF_HEADER } from './api';
import { API_ROUTES, qk } from './apiRoutes';

const getServerFeatureFlags = async (
	projectId: string,
	refreshToken?: string,
	cookieName?: string
): Promise<IServerFeatureFlags> => {
	// The feature flags endpoint is CSRF protected; the token is returned in the
	// response header of the authorized endpoint (same flow as the sso-app).
	const authorizedRes = await adminPortalGet(
		API_ROUTES.authorized(),
		projectId,
		refreshToken,
		cookieName
	);
	const csrfToken = authorizedRes.headers[CSRF_HEADER];

	const requestBody: IReqFeatureFlags = {
		features: (Object.values(FeatureFlagId) as FeatureFlagId[]).map((flag) => ({
			flag,
			defaultVal: FEATURE_FLAGS_DEFAULTS[flag]
		}))
	};

	const res = await adminPortalPost<IReqFeatureFlags, IResFeatureFlags>(
		API_ROUTES.featureFlags(),
		requestBody,
		projectId,
		refreshToken,
		cookieName,
		csrfToken
	);

	const features = {} as IServerFeatureFlags['features'];
	for (const flag of Object.values(FeatureFlagId) as FeatureFlagId[]) {
		// preserve null/undefined so the context can fall back to the local default
		features[flag] = res.data.features[flag];
	}

	return { features };
};

// Must be rendered inside the Descope auth provider since it reads the session.
export const useServerFeatureFlags = () => {
	const { projectId, cookieName } = useProjectCtx();
	const { isAuthenticated } = useSession();
	const { getRefreshToken } = useDescope();

	return useQuery({
		queryKey: qk.featureFlags(),
		queryFn: () =>
			getServerFeatureFlags(projectId!, getRefreshToken(), cookieName),
		enabled: !!projectId && isAuthenticated,
		staleTime: Infinity
	});
};
